import { Link } from 'react-router-dom';
import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ComicCard from '@/components/base/ComicCard';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SEOHead from '@/components/base/SEOHead';
import { comics, categories } from '@/mocks/comics';
import type { Comic } from '@/mocks/comics';

export default function Home() {
  const [selectedCategory, setSelectedCategory] = useState('all');

  const hotComics = comics.filter((c) => c.isHot).slice(0, 5);
  const newComics = comics.filter((c) => c.isNew).slice(0, 5);
  const featuredComic = hotComics[0];

  const filteredComics: Comic[] = selectedCategory === 'all'
    ? comics
    : comics.filter((c) => c.categorySlug === selectedCategory);

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: '韩漫画网',
    url: 'https://www.hanmanhua.com',
    description: '韩漫画网提供无遮韩国免费漫画大全和免费在线阅读韩漫服务，阅读环境纯净无广告，带给您舒适便捷的漫画体验。',
    potentialAction: {
      '@type': 'SearchAction',
      target: 'https://www.hanmanhua.com/search?q={search_term_string}',
      'query-input': 'required name=search_term_string',
    },
  };

  return (
    <>
      <SEOHead
        title="无遮韩国免费漫画大全 | 免费在线阅读韩漫"
        description="韩漫画网提供无遮韩国免费漫画大全和免费在线阅读韩漫服务，涵盖恋爱、奇幻、动作、悬疑等多种分类韩国漫画。阅读环境纯净无广告，带给您舒适便捷的漫画体验。"
        keywords="韩漫画网,无遮韩国免费漫画大全,免费在线阅读韩漫,韩国漫画,韩漫在线"
        structuredData={structuredData}
      />
      <ScrollToTop />
      <Navbar />

      <main>
        {/* Hero Section */}
        <section className="relative h-[520px] md:h-[620px] flex items-center overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Artistic%20abstract%20Korean%20manhwa%20style%20background%20with%20flowing%20ink%20brush%20strokes%20in%20warm%20coral%20and%20amber%20tones%2C%20elegant%20minimalist%20composition%2C%20soft%20gradient%20overlay%2C%20dreamy%20atmosphere%2C%20dark%20edges%2C%20cinematic%20feel&width=1920&height=1080&seq=hero-bg-home-01&orientation=landscape"
            alt="韩漫画网 - 免费韩国漫画在线阅读"
            title="韩漫画网 - 无遮韩国免费漫画大全"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/60"></div>

          <div className="relative w-full px-4 md:px-6">
            <div className="max-w-7xl mx-auto">
              <div className="max-w-2xl animate-fade-in-up">
                <p className="text-accent-400 text-sm md:text-base font-medium mb-3 tracking-wider">
                  —— 韩漫画网 ——
                </p>
                <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold font-heading text-white leading-tight mb-4">
                  无遮韩国免费<br />漫画大全
                </h1>
                <p className="text-base md:text-lg text-white/80 leading-relaxed mb-8 max-w-lg">
                  免费在线阅读韩漫，涵盖恋爱、奇幻、动作、悬疑等多种分类。<strong>阅读环境纯净无广告</strong>，每日更新最新最热门的韩国漫画资源。
                </p>
                <div className="flex flex-col sm:flex-row gap-3">
                  <Link
                    to="/category/all"
                    className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-primary-500 text-white font-medium rounded-full hover:bg-primary-600 transition-all duration-300 whitespace-nowrap"
                  >
                    <i className="ri-book-read-line"></i>
                    开始阅读
                  </Link>
                  <Link
                    to="/about"
                    className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-white/15 text-white font-medium rounded-full hover:bg-white/25 backdrop-blur-sm transition-all duration-300 whitespace-nowrap"
                  >
                    <i className="ri-information-line"></i>
                    了解更多
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Category Navigation */}
        <section className="py-8 bg-background-50 border-b border-background-200">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center gap-3 overflow-x-auto pb-2 scrollbar-hide">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className="flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap bg-background-100 text-foreground-600 hover:bg-primary-500 hover:text-white transition-all duration-200 flex-shrink-0"
                >
                  <i className={`${cat.icon} w-4 h-4 flex items-center justify-center`}></i>
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Featured Comic */}
        {featuredComic && (
          <section className="py-12 md:py-16 bg-background-50">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="flex items-center justify-between mb-8">
                <div>
                  <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-900">
                    今日推荐
                  </h2>
                  <p className="text-sm text-foreground-500 mt-1">编辑精选，不容错过</p>
                </div>
              </div>
              <ComicCard comic={featuredComic} variant="featured" />
            </div>
          </section>
        )}

        {/* Hot Comics */}
        <section className="py-12 md:py-16 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-900">
                  热门漫画
                </h2>
                <p className="text-sm text-foreground-500 mt-1">大家都在看的热门韩漫</p>
              </div>
              <Link
                to="/category/all"
                className="flex items-center gap-1 text-sm text-primary-500 hover:text-primary-600 transition-colors whitespace-nowrap"
              >
                查看全部 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-5" data-product-shop="">
              {hotComics.map((comic, idx) => (
                <div
                  key={comic.id}
                  className="animate-fade-in-up"
                  style={{ animationDelay: `${idx * 0.1}s` }}
                >
                  <ComicCard comic={comic} />
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* New Arrivals */}
        <section className="py-12 md:py-16 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-900">
                  新作上架
                </h2>
                <p className="text-sm text-foreground-500 mt-1">最新上线的韩漫作品</p>
              </div>
              <Link
                to="/category/all"
                className="flex items-center gap-1 text-sm text-primary-500 hover:text-primary-600 transition-colors whitespace-nowrap"
              >
                查看全部 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-5" data-product-shop="">
              {newComics.map((comic, idx) => (
                <div
                  key={comic.id}
                  className="animate-fade-in-up"
                  style={{ animationDelay: `${idx * 0.1}s` }}
                >
                  <ComicCard comic={comic} />
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* All Comics */}
        <section className="py-12 md:py-16 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-900">
                  全部漫画
                </h2>
                <p className="text-sm text-foreground-500 mt-1">浏览所有韩漫作品</p>
              </div>
            </div>

            {/* Category Tabs */}
            <div className="flex items-center gap-2 mb-6 flex-wrap">
              {categories.map((cat) => (
                <button
                  key={cat.slug}
                  onClick={() => setSelectedCategory(cat.slug)}
                  className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                    selectedCategory === cat.slug
                      ? 'bg-primary-500 text-white'
                      : 'bg-background-50 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  {cat.name}
                </button>
              ))}
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5" data-product-shop="">
              {filteredComics.map((comic, idx) => (
                <div
                  key={comic.id}
                  className="animate-fade-in-up"
                  style={{ animationDelay: `${idx * 0.05}s` }}
                >
                  <ComicCard comic={comic} />
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="py-14 md:py-20 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900">
                为什么选择韩漫画网
              </h2>
              <p className="text-sm text-foreground-500 mt-2 max-w-xl mx-auto">
                我们致力于为韩漫爱好者提供最优质的免费阅读体验
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center p-6 rounded-lg bg-background-100">
                <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-accent-100 flex items-center justify-center">
                  <i className="ri-shield-check-line text-2xl text-accent-600"></i>
                </div>
                <h3 className="text-base font-semibold text-foreground-900 mb-2">纯净无广告</h3>
                <p className="text-sm text-foreground-500 leading-relaxed">
                  阅读环境完全无广告干扰，让您沉浸在漫画的世界中，享受最纯粹的阅读体验。
                </p>
              </div>
              <div className="text-center p-6 rounded-lg bg-background-100">
                <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-primary-100 flex items-center justify-center">
                  <i className="ri-flashlight-line text-2xl text-primary-600"></i>
                </div>
                <h3 className="text-base font-semibold text-foreground-900 mb-2">每日更新</h3>
                <p className="text-sm text-foreground-500 leading-relaxed">
                  每天准时更新最新话，热门连载持续跟进，第一时间为您呈现最新韩漫内容。
                </p>
              </div>
              <div className="text-center p-6 rounded-lg bg-background-100">
                <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-secondary-100 flex items-center justify-center">
                  <i className="ri-stack-line text-2xl text-secondary-600"></i>
                </div>
                <h3 className="text-base font-semibold text-foreground-900 mb-2">海量资源</h3>
                <p className="text-sm text-foreground-500 leading-relaxed">
                  涵盖恋爱、奇幻、动作、悬疑等多个分类，丰富的漫画资源满足您不同的阅读口味。
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-14 md:py-20 bg-background-100">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900">
                常见问题
              </h2>
            </div>
            <div className="space-y-3">
              {[
                { q: '韩漫画网的漫画都是免费的吗？', a: '是的！韩漫画网上所有的韩国漫画资源均为免费阅读，无需付费解锁，也没有任何隐藏费用。我们致力于为韩漫爱好者提供完全免费的阅读体验。' },
                { q: '网站上的漫画多久更新一次？', a: '热门连载漫画通常每日更新，新章节上线后我们会第一时间同步。您可以在漫画详情页查看最新的更新时间和章节列表。' },
                { q: '如何在手机上阅读韩漫？', a: '韩漫画网采用响应式设计，完美适配手机、平板和电脑。您可以直接在手机浏览器中打开我们的网站，即可享受流畅的移动端漫画阅读体验。' },
                { q: '网站真的有广告吗？', a: '韩漫画网承诺阅读环境纯净无广告！我们不会在阅读页面放置任何弹窗广告、横幅广告或视频广告，确保您拥有最舒适的阅读体验。' },
              ].map((faq, idx) => (
                <details key={idx} className="group bg-background-50 rounded-lg p-5 cursor-pointer">
                  <summary className="flex items-center justify-between text-sm font-medium text-foreground-900 list-none">
                    {faq.q}
                    <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform w-5 h-5 flex items-center justify-center"></i>
                  </summary>
                  <p className="mt-3 text-sm text-foreground-600 leading-relaxed">{faq.a}</p>
                </details>
              ))}
            </div>
            <script
              type="application/ld+json"
              dangerouslySetInnerHTML={{
                __html: JSON.stringify({
                  '@context': 'https://schema.org',
                  '@type': 'FAQPage',
                  mainEntity: [
                    { '@type': 'Question', name: '韩漫画网的漫画都是免费的吗？', acceptedAnswer: { '@type': 'Answer', text: '是的！韩漫画网上所有的韩国漫画资源均为免费阅读，无需付费解锁，也没有任何隐藏费用。' } },
                    { '@type': 'Question', name: '网站上的漫画多久更新一次？', acceptedAnswer: { '@type': 'Answer', text: '热门连载漫画通常每日更新，新章节上线后我们会第一时间同步。' } },
                    { '@type': 'Question', name: '如何在手机上阅读韩漫？', acceptedAnswer: { '@type': 'Answer', text: '韩漫画网采用响应式设计，完美适配手机、平板和电脑。直接在手机浏览器中打开即可享受流畅的移动端漫画阅读体验。' } },
                    { '@type': 'Question', name: '网站真的有广告吗？', acceptedAnswer: { '@type': 'Answer', text: '韩漫画网承诺阅读环境纯净无广告，不会在阅读页面放置任何弹窗广告、横幅广告或视频广告。' } },
                  ],
                }),
              }}
            />
          </div>
        </section>

        {/* CTA Section */}
        <section className="relative py-16 md:py-24 overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Warm%20artistic%20gradient%20background%20with%20soft%20coral%20and%20amber%20tones%2C%20elegant%20flowing%20lines%2C%20minimalist%20abstract%20composition%2C%20dreamy%20atmosphere%2C%20modern%20Korean%20aesthetic&width=1920&height=800&seq=cta-bg-home-02&orientation=landscape"
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-black/70"></div>
          <div className="relative max-w-7xl mx-auto px-4 md:px-6 text-center">
            <h2 className="text-2xl md:text-4xl font-bold font-heading text-white mb-4">
              开启您的韩漫之旅
            </h2>
            <p className="text-white/80 text-sm md:text-base max-w-lg mx-auto mb-8">
              加入数百万韩漫爱好者的行列，在韩漫画网发现您最爱的韩国漫画作品。
            </p>
            <Link
              to="/category/all"
              className="inline-flex items-center gap-2 px-8 py-3.5 bg-primary-500 text-white font-medium rounded-full hover:bg-primary-600 transition-all duration-300 whitespace-nowrap"
            >
              <i className="ri-book-open-line"></i>
              立即免费阅读
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}